import {
  AddToCartButton,
  BuyNowButton,
  Link,
  ProductOptionsProvider,
  useProductOptions,
} from '@shopify/hydrogen';
import type {ProductVariant} from '@shopify/hydrogen/storefront-api-types';
// @ts-expect-error incompatibility with node16 resolution
import Tippy from '@tippyjs/react/headless';
import clsx from 'clsx';
import {ReactNode, useMemo} from 'react';
import type {ProductWithNodes} from '../../types';
import Tooltip from '../elements/Tooltip';
// import ProductTooltip from '../product/Tooltip.client';
// import CartIcon from '../icons/Cart';
// import CreditCardIcon from '../icons/CreditCard';

type Props = {
  children?: ReactNode;
  initialVariantId?: ProductVariant['id'];
  linkAction: 'addToCart' | 'buyNow' | 'link';
  quantity?: number;
  storefrontProduct: ProductWithNodes;
};

export default function ProductInlineLink({
  children,
  initialVariantId,
  linkAction,
  quantity,
  storefrontProduct,
}: Props) {
  return (
    <ProductOptionsProvider
      data={storefrontProduct}
      initialVariantId={initialVariantId}
    >
      <ProductInlineLinkContent
        linkAction={linkAction}
        quantity={quantity}
        storefrontProduct={storefrontProduct}
      >
        {children}
      </ProductInlineLinkContent>
    </ProductOptionsProvider>
  );
}

function ProductInlineLinkContent({
  children,
  linkAction,
  quantity,
  storefrontProduct,
}: Props) {
  const {handle, title} = storefrontProduct;
  const {selectedVariant} = useProductOptions();

  // Return text only if variant cannot be found
  if (!selectedVariant?.id) {
    return <>{children}</>;
  }

  // Return strikethrough text and sold out label if variant is not for sale AND we're using a 'buyNow' or 'addToCart' action
  if (!selectedVariant.availableForSale && linkAction !== 'link') {
    return (
      <span className='bg-soldOut'>
        {children}
        <span className="ml-fontSpace text-main">
          [Sold out]
        </span>
      </span>
    );
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const LinkContent = useMemo(
    () => (
      <span
        className={clsx(
          // 'inline-flex place-content-center items-center',
          'bg-highlight'
        )}
      >
        {children}
        {/* {linkAction === 'addToCart' && <CartIcon className="ml-[0.25em]" />}
        {linkAction === 'buyNow' && <CreditCardIcon className="ml-[0.25em]" />} */}
      </span>
    ),
    [children, linkAction],
  );

  return (
    <Tippy
      interactive={linkAction === 'link'}
      placement="top-start"
      render={() => {
        if (linkAction === 'addToCart') {
          return <Tooltip label={`Add to cart [×${quantity}]`} tone="main" />;
          // return <Tooltip label={`Add to cart [${title}]`} tone="main" />;
        }
        if (linkAction === 'buyNow') {
          return <Tooltip label={`Buy now [×${quantity}]`} tone="shopPay" />;
          // return <Tooltip label={`Buy now [${title}]`} tone="shopPay" />;
        }
        if (linkAction === 'link') {
          return <Tooltip label={`Go to [${title}]`} tone="black" />;
          // return <ProductTooltip storefrontProduct={storefrontProduct} />;
        }
        return null;
      }}
    >
      <span>
        {linkAction === 'addToCart' && (
          <AddToCartButton
            quantity={quantity}
            style={{fontWeight: 'inherit', letterSpacing: 'inherit'}}
            variantId={selectedVariant.id}
          >
            {LinkContent}
          </AddToCartButton>
        )}
        {linkAction === 'buyNow' && (
          <BuyNowButton
            quantity={quantity}
            style={{fontWeight: 'inherit', letterSpacing: 'inherit'}}
            variantId={selectedVariant.id}
          >
            {LinkContent}
          </BuyNowButton>
        )}
        {linkAction === 'link' && (
          <Link className='inline' to={`/products/${handle}`}>{LinkContent}</Link>
        )}
      </span>
    </Tippy>
  );
}
